import { QIcon_new_line } from '@repo/icons/index'
import { sortBy } from 'lodash'
import { useEffect, useRef } from 'react'
import { IKeyExpressionCategory } from '../../../../model/feedback.interface'
import useTranslation from '../../../../modules/i18n/useTranslation'

interface Range {
  index: number
  length: number
}
interface IKeyExpressionCard {
  title: string
  before?: string
  after?: string
  tutorName: string
  tutorsComment: string
  keyExpressions?: Range[]
}
type CategoryKeyType = keyof typeof IKeyExpressionCategory
type KoreanMapType = {
  [key in CategoryKeyType]: string
}
const KoreanMap: KoreanMapType = {
  'Words and Expressions': '단어와 표현',
  'Grammatical Accuracy': '문법 정확성',
  'Sentence Variation': '문장 다양성',
  'Tone and Context': '톤과 콘텍스트',
  Pronunciation: '발음',
}
const wrapTextWithSpan = (origin, ac, beforeIndex, index, length) => {
  const before = origin.substring(beforeIndex, index)
  const target = origin.substring(index, index + length)
  const after = origin.substring(index + length)
  const accumulator = `${ac}${before}<span class="feedback-key-expression">${target}</span>`

  return { accumulator, rest: after }
}
const KeyExpressionCard = ({ title, before, after, tutorName, tutorsComment, keyExpressions }: IKeyExpressionCard) => {
  const afterTextWrapperRef = useRef(null)
  const keyExpressionsSorted = sortBy(keyExpressions, (o) => o.index)

  useEffect(() => {
    const targetText = after
    if (targetText && keyExpressionsSorted.length > 0 && afterTextWrapperRef.current) {
      const reduced = keyExpressionsSorted.reduce(
        (ac, cr, i, arr) => {
          const beforeIndex = i === 0 ? 0 : arr[i - 1].index + arr[i - 1].length
          const { accumulator, rest } = wrapTextWithSpan(targetText, ac.before, beforeIndex, cr.index, cr.length)
          return { origin: targetText, before: accumulator, rest }
        },
        { origin: targetText, before: '', rest: '' }
      )
      const applied = reduced.before + reduced.rest
      afterTextWrapperRef.current.innerHTML = applied
    } else if (targetText && keyExpressionsSorted.length == 0 && afterTextWrapperRef.current) {
      afterTextWrapperRef.current.innerHTML = `<span>${targetText}</span>`
    }
  }, [after, keyExpressionsSorted, afterTextWrapperRef.current])

  return (
    <div className="border-1 mb-[16px] flex flex-col items-start justify-start rounded-[8px] border-blue-400 p-[16px]">
      <Title title={title} />
      {!!before && <div className="keep-all font-16px mt-[24px] text-gray-800">{before}</div>}
      {!!after && (
        <div className="mt-[12px] flex flex-row items-start justify-start">
          <QIcon_new_line className="mt-[4px] mr-[8px]" />
          <div className="keep-all font-16px text-gray-800" ref={afterTextWrapperRef} />
        </div>
      )}
      <TutorsComment tutorName={tutorName} tutorsComment={tutorsComment} />
    </div>
  )
}
const Title = ({ title }) => {
  const { isLocaleKO } = useTranslation()
  const koText = isLocaleKO ? KoreanMap[title] : '' //TODO: 워딩적용

  return (
    <div className="rounded-[4px] bg-blue-50 px-[10px] py-[4px] text-[14px] font-bold text-blue-400">
      {title}
      {koText && <span className="ml-[8px] font-normal">{koText}</span>}
    </div>
  )
}
const TutorsComment = ({ tutorName, tutorsComment }: { tutorName: string; tutorsComment: string }) => {
  return (
    <div className="keep-all mt-[16px] flex w-full flex-col rounded-[10px] bg-gray-50 p-[16px]">
      <span className="px-[6px] py-[2px] text-[12px] font-bold text-gray-600">{tutorName}'s Comment</span>
      <div className="mt-[8px] text-[16px] text-gray-800">{tutorsComment}</div>
    </div>
  )
}
export default KeyExpressionCard
